import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import DialogContent from '@zenbusiness/zui/DialogContent';
import Grid from '@zenbusiness/zui/Grid';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';

import * as Styled from './ChangeBillingIntervalAnnualComparison.style';

import { useChangeRequestContext, useSetChangeRequest } from '../hooks/useChangeRequestContext';
import getSharedAsset from '../utils/sharedAssetMap';

export default function ChangeBillingIntervalAnnualComparison({
  subscription,
  serviceDisplayName,
  products,
  billingChangeSavings,
}) {
  const [itemSelected, setItemSelected] = useState(null);
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { changeFlow } = useChangeRequestContext();
  const { setChangeFlow } = useSetChangeRequest();
  const { modalFlow } = changeFlow;
  const { changeBillingIntervalAnnual } = modalFlow;

  useEffect(() => {
    if (itemSelected !== null) {
      return;
    }
    if (!products?.length) {
      return;
    }
    const currentProduct = products.find((p) => p.pricing?.interval === subscription?.period);
    if (currentProduct) {
      setItemSelected(currentProduct.pricing.interval);
    }
  }, [products, subscription?.period, itemSelected]);

  const assetUrl = getSharedAsset(subscription?.service || '');
  const submitDisabled = itemSelected === subscription?.period;

  const today = new Date();
  const effectiveDate = [
    (today.getMonth() + 1).toString().padStart(2, '0'),
    today.getDate().toString().padStart(2, '0'),
    today.getFullYear(),
  ].join('/');

  const handleContinue = () => {
    if (submitDisabled) {
      return;
    }
    setChangeFlow((prevState) => ({
      ...prevState,
      orderBillingItemUuid: subscription?.orderBillingItemUuid,
      interval: itemSelected,
      modalFlow: {
        ...modalFlow,
        changeBillingIntervalAnnual: {
          ...changeBillingIntervalAnnual,
          stepOne: false,
          stepTwo: true,
        },
      },
    }));
  };

  return (
    <>
      <DialogContent>
        <Box mb={4} textAlign="center">
          <Styled.ComparisonTitle size="xl" weight="bold" align="center">
            <FormattedMessage id="dashboard.subscriptions.billingChange.comparisonTitle" />
          </Styled.ComparisonTitle>
          <Text size="md" color="secondary" align="center">
            <FormattedMessage id="dashboard.subscriptions.billingChange.comparisonSubtitle" />
          </Text>
        </Box>

        <Styled.ServiceIconStack alignItems="center">
          <Styled.ServiceIcon component="img" src={assetUrl} alt={serviceDisplayName} />
          <Styled.ServiceName size="lg" weight="bold" align="center">
            {serviceDisplayName}
          </Styled.ServiceName>
        </Styled.ServiceIconStack>

        {products?.length > 0 && (
          <Styled.ProductGrid container spacing={2}>
            {products.map(({ pricing: { uuid, interval, amount } }) => (
              <Grid item xs={12} sm={6} key={`product-pricing-${uuid}`}>
                <Box
                  data-testid="set-item-selected"
                  onClick={() => setItemSelected(interval)}
                  sx={{
                    border: '1px solid',
                    borderColor: itemSelected === interval ? theme.palette.text.primary : theme.palette.divider,
                    borderRadius: '1rem',
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor:
                      itemSelected === interval ? theme.palette.action.selected : theme.palette.background.paper,
                    color: 'inherit',
                  }}
                >
                  <Styled.ProductIntervalLabel weight="bold" align="center">
                    {interval === 'annual' ? (
                      <FormattedMessage id="dashboard.subscriptions.billingChange.annually" />
                    ) : (
                      interval
                    )}
                  </Styled.ProductIntervalLabel>
                  <Styled.ProductPriceText align="center">
                    {`$${amount} / ${
                      interval === 'annual'
                        ? formatMessage({ id: 'dashboard.subscriptions.price.periodYear' }).toLowerCase()
                        : formatMessage({ id: 'dashboard.subscriptions.price.periodMonth' }).toLowerCase()
                    }`}
                  </Styled.ProductPriceText>
                  {interval === subscription?.period && (
                    <Box mt={1}>
                      <Styled.ProductChip
                        label={formatMessage({ id: 'dashboard.subscriptions.billingChange.currentPlan' })}
                        size="small"
                        color="default"
                      />
                      <Styled.ProductNoteText align="center">
                        {formatMessage(
                          { id: 'dashboard.subscriptions.billingChange.renews' },
                          { date: subscription.termEndDate },
                        )}
                      </Styled.ProductNoteText>
                    </Box>
                  )}
                  {interval !== subscription?.period && interval === 'annual' && (
                    <Box mt={1}>
                      <Styled.ProductChip
                        label={formatMessage(
                          { id: 'dashboard.subscriptions.menu.savePct' },
                          { pct: billingChangeSavings },
                        )}
                        size="small"
                        color="success"
                      />
                      <Styled.ProductNoteText align="center">
                        {formatMessage(
                          { id: 'dashboard.subscriptions.billingChange.effective' },
                          { date: effectiveDate },
                        )}
                      </Styled.ProductNoteText>
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
          </Styled.ProductGrid>
        )}
      </DialogContent>

      <Styled.ModalActions>
        <Button
          variant="contained"
          disabled={submitDisabled}
          onClick={handleContinue}
          clickstreamProps={{ elementName: 'billing_change_continue_to_review' }}
          data-testid="submit-product-selection"
        >
          <FormattedMessage id="dashboard.subscriptions.billingChange.continueToReview" />
        </Button>
      </Styled.ModalActions>
    </>
  );
}

ChangeBillingIntervalAnnualComparison.propTypes = {
  subscription: PropTypes.shape({
    orderBillingItemUuid: PropTypes.string,
    service: PropTypes.string,
    period: PropTypes.string,
    termEndDate: PropTypes.string,
  }).isRequired,
  serviceDisplayName: PropTypes.string,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      pricing: PropTypes.shape({
        uuid: PropTypes.string,
        interval: PropTypes.string,
        amount: PropTypes.number,
      }),
    }),
  ),
  billingChangeSavings: PropTypes.number,
};
